import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { HealthStatus, SupportTool } from "../types";
import { supportToolsApi } from "../utils/apiClient";

// Constants
const HEALTH_CHECK_TIMEOUT_MS = 5000;
const VALID_HEALTH_STATUSES: HealthStatus[] = ["healthy", "disabled", "unhealthy"];
const INITIAL_HEALTH_STATS = {
  healthy: 0,
  unhealthy: 0,
  disabled: 0,
  checking: 0,
  unknown: 0,
};

interface ToolHealthState {
  toolId: string;
  status: HealthStatus;
  lastChecked: Date | null;
  error?: string;
}

export const useToolHealthCheck = (tools: SupportTool[]) => {
  const [healthStates, setHealthStates] = useState<Record<string, ToolHealthState>>({});
  const [isCheckingAny, setIsCheckingAny] = useState(false);

  // Concurrency protection and cleanup refs
  const abortControllerRef = useRef<AbortController | null>(null);
  const timeoutRefs = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const perToolControllersRef = useRef<Map<string, AbortController>>(new Map());

  // Get tools that have health check endpoints
  const toolsWithHealthCheck = useMemo(() => tools.filter((tool) => tool.healthEndpoint), [tools]);

  // Cleanup function for timeouts and abort controller
  const cleanup = useCallback(() => {
    timeoutRefs.current.forEach((timeoutId) => {
      clearTimeout(timeoutId);
    });
    timeoutRefs.current.clear();

    abortControllerRef.current?.abort();
    abortControllerRef.current = null;

    perToolControllersRef.current.forEach((toolController) => {
      toolController.abort();
    });
    perToolControllersRef.current.clear();
  }, []);

  const checkAllToolsHealth = useCallback(async () => {
    // Prevent concurrent health checks
    if (abortControllerRef.current) {
      return;
    }

    setIsCheckingAny(true);

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      // Set all tools to checking state, preserving lastChecked
      setHealthStates((prev) => ({
        ...prev,
        ...Object.fromEntries(
          toolsWithHealthCheck.map((tool) => [
            tool.id,
            {
              toolId: tool.id,
              status: "checking",
              lastChecked: prev[tool.id]?.lastChecked || null,
            },
          ]),
        ),
      }));

      // Check each tool
      const promises = toolsWithHealthCheck.map(async (tool) => {
        const toolController = new AbortController();
        const timeoutId = setTimeout(() => toolController.abort(), HEALTH_CHECK_TIMEOUT_MS);
        timeoutRefs.current.set(tool.id, timeoutId);
        perToolControllersRef.current.set(tool.id, toolController);

        try {
          if (!tool.healthEndpoint) {
            throw new Error(`No health endpoint configured for tool: ${tool.id}`);
          }

          const response = await supportToolsApi.get(tool.healthEndpoint, {
            signal: toolController.signal,
          });

          // Response data is already parsed by axios
          const healthData = response.data;
          const status: HealthStatus =
            healthData?.status && VALID_HEALTH_STATUSES.includes(healthData.status) ? healthData.status : "unhealthy";

          return { toolId: tool.id, status, lastChecked: new Date() };
        } catch (error) {
          return {
            toolId: tool.id,
            status: "unhealthy" as HealthStatus,
            lastChecked: new Date(),
            error: error instanceof Error ? error.message : `Health check failed: ${String(error)}`,
          };
        } finally {
          const timeout = timeoutRefs.current.get(tool.id);
          if (timeout) {
            clearTimeout(timeout);
            timeoutRefs.current.delete(tool.id);
          }
          perToolControllersRef.current.delete(tool.id);
        }
      });

      const results = await Promise.all(promises);

      if (!controller.signal.aborted) {
        const newStates = Object.fromEntries(results.map((result) => [result.toolId, result])) as Record<
          string,
          ToolHealthState
        >;
        setHealthStates((prev) => ({ ...prev, ...newStates }));
      }
    } catch (error) {
      if (!controller.signal.aborted) {
        console.error("Health check failed:", error);
      }
    } finally {
      cleanup();
      setIsCheckingAny(false);
    }
  }, [toolsWithHealthCheck, cleanup]);

  const getToolHealthStatus = useCallback(
    (toolId: string): HealthStatus => healthStates[toolId]?.status || "unknown",
    [healthStates],
  );

  // Run initial health check on mount and cleanup on unmount
  useEffect(() => {
    checkAllToolsHealth();
    return cleanup;
  }, [checkAllToolsHealth, cleanup]);

  // Calculate health stats for the status bar
  const healthStats = useMemo(
    () =>
      toolsWithHealthCheck.reduce(
        (stats, tool) => {
          const status = healthStates[tool.id]?.status || "unknown";
          stats[status] = stats[status] + 1;
          return stats;
        },
        { ...INITIAL_HEALTH_STATS },
      ),
    [toolsWithHealthCheck, healthStates],
  );

  return {
    healthStates,
    getToolHealthStatus,
    refreshHealthChecks: checkAllToolsHealth,
    healthStats,
    isCheckingAny,
    toolsWithHealthCheck,
  };
};
